import { calculateLocale } from "@pandas/core-ui";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { CountryCode, getCountries, getCountryCallingCode, isValidPhoneNumber } from "libphonenumber-js";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import Button from "@/components/core/Buttons/Button";
import InputField from "@/components/core/FormControls/InputField";
import PhoneInput from "@/components/core/PhoneInput/PhoneInput";
import { useTagManager } from "@/hooks/useTagManager";
import { requireAuthWithDevice } from "@/lib/guards";
import { platformApi } from "@/services";
import { useStore } from "@/store";
import { logError } from "@/utils/logging/error";

export const Route = createFileRoute("/$locale/$partner/recycling/form")({
	beforeLoad: requireAuthWithDevice,
	component: RecyclingForm,
});

const countryCodes: readonly string[] = getCountries();
function isCountryCode(value: string | undefined): value is CountryCode {
	if (!value) return false;
	return countryCodes.includes(value);
}

function RecyclingForm() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { locale: routeLocale, partner: routePartner } = useParams({ from: "/$locale/$partner" });
	const setCustomerInfo = useStore((state) => state.setCustomerInfo);
	const fullName = useStore((state) => state.fullName);
	const email = useStore((state) => state.email);
	const phoneNumber = useStore((state) => state.phoneNumber) ?? "";
	const countryCode = useStore((state) => state.countryCode);
	const locale = useStore((state) => state.location?.locale);
	const defaultCountryCode = locale?.split("-")[1]?.toUpperCase();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const identifier = useStore((state) => state.deviceInfo.imei);
	const locationId = useStore((state) => state.location?.id);
	const sessionId = useStore((state) => state.sessionId);

	const effectiveCountryCode = countryCode ?? defaultCountryCode;
	const validatedCountryCode = isCountryCode(effectiveCountryCode) ? effectiveCountryCode : undefined;

	const isPhoneValid = validatedCountryCode ? isValidPhoneNumber(phoneNumber, validatedCountryCode) : false;

	useTagManager("Step Form");

	const isValidEmail = (email: string | null) => {
		return z.string().email().safeParse(email).success;
	};

	const isValidFullname = (fullName: string | null) => {
		const fullNameSchema = z
			.string()
			.min(3)
			.refine((name) => name.trim().split(" ").filter(Boolean).length >= 2);
		return fullNameSchema.safeParse(fullName).success;
	};

	const invalidEmailError =
		isValidEmail(email) || email === null || email === "" ? null : t("InputField.Invalid Email");

	const invalidFullNameError =
		isValidFullname(fullName) || fullName === null || fullName === "" ? null : t("InputField.Invalid FullName");

	const isButtonEnabled = phoneNumber !== "" && isPhoneValid && isValidEmail(email) && isValidFullname(fullName);

	const confirmRecycling = async () => {
		if (!identifier || !locationId) return;
		setLoading(true);
		try {
			await platformApi.completeRecycling(
				identifier,
				locationId,
				"trade_in",
				calculateLocale(i18n.language, undefined),
				fullName ?? "",
				email ?? "",
				`${validatedCountryCode ? getCountryCallingCode(validatedCountryCode) : ""}${phoneNumber}`,
				sessionId ?? undefined,
			);
			navigate({ to: "/$locale/$partner/recycling/success", params: { locale: routeLocale, partner: routePartner } });
		} catch (error) {
			logError(error, "Could not complete recycling");
			setError(t("Error.Oops something went wrong"));
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="mx-auto max-w-[81rem]">
			<div className="max-w-[35rem] m-auto">
				<h1 className="text-center text-3xl font-semibold mt-16 mb-9">{t("CustomerFormPage.Customer Info")}</h1>
				<div className="mb-5">
					<InputField
						placeholder={t("InputField.FullName")}
						label={t("InputField.FullName")}
						type="text"
						align="left"
						error={invalidFullNameError}
						value={fullName ?? ""}
						onChange={(e) => {
							setCustomerInfo({ fullName: e.target.value });
						}}
					/>
				</div>
				<div className="mb-4">
					<InputField
						placeholder={t("InputField.Email")}
						type="email"
						value={email ?? ""}
						error={invalidEmailError}
						align="left"
						label={t("InputField.Email")}
						onChange={(e) => {
							setCustomerInfo({ email: e.target.value });
						}}
					/>
				</div>
				<div className="mb-5">
					<PhoneInput
						placeholder={t("InputField.Phone Number") ?? ""}
						align="left"
						label={t("InputField.Phone Number") ?? ""}
						onDone={(value) => {
							setCustomerInfo({ phoneNumber: value });
						}}
						onCountryCodeChange={(code: string) => {
							if (isCountryCode(code)) {
								setCustomerInfo({ countryCode: code });
							}
						}}
						hasError={Boolean(!isPhoneValid && phoneNumber && phoneNumber !== "" && countryCode !== undefined)}
					/>
				</div>
				<div className="mx-auto w-fit mt-12">
					<Button
						label={t("Buttons.Complete Recycling Process")}
						size="large"
						disabled={!isButtonEnabled || loading}
						loading={loading}
						onClick={confirmRecycling}
					/>
				</div>
				{error && <p className="text-center pt-6 text-red">{error}</p>}
			</div>
		</div>
	);
}
